import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Mail, ArrowLeft } from "lucide-react";
import { api, setToken, getToken } from "@/library/lib/api";
import { toast } from "sonner";

const AdminLogin = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If already authenticated, skip login and go to library admin dashboard
  useEffect(() => {
    if (getToken()) nav("/library/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success("تم تسجيل الدخول بنجاح");
      nav("/library/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center p-4 hero-gradient relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-gold/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand-light/30 rounded-full blur-3xl" />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10"
      >
        <a href="/" data-testid="library-login-back-portal" className="text-xs text-slate-500 hover:text-brand font-medium flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3 rotate-180" /> العودة إلى بوابة الأنظمة</a>
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center mb-5 shadow-lg">
          <ShieldCheck className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-brand-dark">لوحة الإدارة</h1>
        <p className="text-sm text-slate-500 mt-1 mb-7">سجّل الدخول للوصول إلى أدوات إدارة المكتبة الرقمية.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">البريد الإلكتروني</label>
            <div className="relative">
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@madaris.edu"
                required
                className="w-full pe-10 ps-4 py-3 rounded-xl border border-slate-200 focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">كلمة المرور</label>
            <div className="relative">
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full pe-10 ps-4 py-3 rounded-xl border border-slate-200 focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none text-sm"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="w-full mt-7 bg-brand hover:bg-brand-light text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-60"
        >
          {loading ? "جارٍ التحقق..." : "تسجيل الدخول"}
        </button>
      </motion.form>
    </div>
  );
};

export default AdminLogin;
